/** One listing drawn as a price pin. */
export type MapPin = {
  addressId: string;
  latitude: number;
  longitude: number;
  listPriceCents: number | null;
  isLiked: boolean;
};

export type MapEnvelope = { north: number; south: number; east: number; west: number };

/** A group of homes drawn as a count bubble. */
export type MapClusterPin = {
  /** Stable server bucket or client group identity. */
  id: string;
  latitude: number;
  longitude: number;
  count: number;
  expansionZoom: number;
  envelope: MapEnvelope | null;
};

const trimTrailingZero = (value: string) => value.replace(/\.0$/, '');

/** Cluster counts: exact below 1,000, then 1.2K, 10K, 1M, 1.5M. */
export const formatClusterCount = (count: number): string => {
  if (count < 1_000) {
    return String(count);
  }
  // Rounding decides the unit, so 999,500 reads as 1M rather than 1000K.
  if (count < 999_500) {
    const thousands = count / 1_000;
    return thousands < 10
      ? `${trimTrailingZero(thousands.toFixed(1))}K`
      : `${Math.round(thousands)}K`;
  }
  return `${trimTrailingZero((count / 1_000_000).toFixed(1))}M`;
};

/**
 * Price pin labels, copied from the iOS PriceFormatter: whole dollars under
 * $1,000, rounded thousands to $999,499, one decimal in millions under $10M,
 * whole millions above. A missing price reads "Soon" because only
 * available-soon homes lack one.
 */
export const formatPinPrice = (listPriceCents: number | null): string => {
  if (listPriceCents == null) {
    return 'Soon';
  }
  const dollars = listPriceCents / 100;
  if (dollars <= 0) {
    return 'Call';
  }
  if (dollars < 1_000) {
    return `$${Math.round(dollars)}`;
  }
  if (dollars < 999_500) {
    return `$${Math.round(dollars / 1_000)}K`;
  }
  if (dollars < 10_000_000) {
    return `$${trimTrailingZero((dollars / 1_000_000).toFixed(1))}M`;
  }
  return `$${Math.round(dollars / 1_000_000)}M`;
};
